// =============================================
// 1. UPDATED useReportsData HOOK TO FETCH SPLITS
// hooks/useReportsData.js
// =============================================

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

export const useReportsData = (currentTenant) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [rawData, setRawData] = useState([])
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    clientId: 'all',
    projectId: 'all',
    userId: 'all',
    status: 'all',
    billableOnly: false
  })

  // Debounce filters to prevent excessive API calls
  const debouncedFilters = useDebounce(filters, 300)

  // Memoized filtered data - client-side filtering for better performance
  const data = useMemo(() => {
    if (!rawData.length) return []
    
    return rawData.filter(entry => {
      if (debouncedFilters.startDate && entry.entry_date < debouncedFilters.startDate) return false
      if (debouncedFilters.endDate && entry.entry_date > debouncedFilters.endDate) return false
      if (debouncedFilters.clientId !== 'all' && entry.client_id !== debouncedFilters.clientId) return false
      if (debouncedFilters.projectId !== 'all' && entry.project_id !== debouncedFilters.projectId) return false
      if (debouncedFilters.userId !== 'all' && entry.user_id !== debouncedFilters.userId) return false
      if (debouncedFilters.status !== 'all' && entry.status !== debouncedFilters.status) return false
      if (debouncedFilters.billableOnly && !entry.is_billable) return false
      return true
    })
  }, [rawData, debouncedFilters])

  // Fetch data function with splits
  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return

    try {
      setLoading(true)
      setError(null)

      // First, fetch time entries with basic relations
const { data: timeEntries, error: fetchError } = await supabase
  .from('time_entries')
  .select(`
    *,
    user_profiles:user_id (
      id,
      full_name,
      email
    ),
    clients:client_id (
      id,
      name
    ),
    projects:project_id (
      id,
      name,
      budget_hours,
      budget_amount,
      start_date,
      end_date
    ),
    time_entry_splits (
      id,
      description,
      duration_hours,
      is_billable,
      hourly_rate,
      created_at
    )
  `)
  .eq('tenant_id', currentTenant.id)
  .neq('status', 'rejected')
  .order('entry_date', { ascending: false })

      if (fetchError) throw fetchError

      // Then, fetch tenant memberships separately to get bill_rate_role
      const { data: memberships, error: membershipError } = await supabase
        .from('tenant_memberships')
        .select('user_id, bill_rate_role')
        .eq('tenant_id', currentTenant.id)
        .eq('status', 'active')

      if (membershipError) throw membershipError

      // Create a lookup map for bill rate roles
      const billRateMap = new Map()
      memberships?.forEach(membership => {
        billRateMap.set(membership.user_id, membership.bill_rate_role)
      })

      // Transform the data for easier use
      const transformedData = (timeEntries || []).map(entry => ({
  ...entry,
  user_name: entry.user_profiles?.full_name || entry.user_profiles?.email || 'Unknown User',
  client_name: entry.clients?.name || 'Unknown Client',
  project_name: entry.projects?.name || 'Unknown Project',
  project_budget_hours: entry.projects?.budget_hours || null,
  project_budget_amount: entry.projects?.budget_amount || null,
  project_start_date: entry.projects?.start_date || null,
  project_end_date: entry.projects?.end_date || null,
  bill_rate_role: billRateMap.get(entry.user_id) || null,
  splits: entry.time_entry_splits || []
}))

      setRawData(transformedData)

    } catch (err) {
      setError(err.message)
      console.error('Error fetching report data:', err)
    } finally {
      setLoading(false)
    }
  }, [currentTenant?.id])

  // Load data on mount and when tenant changes
  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refetch = useCallback(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    loading,
    error,
    filters,
    setFilters,
    refetch
  }
}


export default useReportsData